// Storage service now using SQLite via storageAdapter (migrated from IndexedDB)
//
// Convention: the storageAdapter's `setValue(key, null)` is treated as
// "delete the row". This matches the key-value-store idiom and avoids the
// SQLite `NOT NULL` constraint on `main_store.value`. Any caller that
// wants to clear a key should pass `null`, not an empty string or `{}`.
import store from "@/store/index";

// Cache variables
let _stockCache = null;

let _fastLineCache = null;
let _stockSettingListCache = null;
let _stockSettingMapCache = null;
import data from "bootstrap/js/src/dom/data";
import axios from "axios";
import { responses } from "@/utils/responses";
import router from "@/router";
import { nextTick } from 'vue'
import { getValue as getValueAdapter, setValue as setValueAdapter } from './storageAdapter';
import StockRepository from "@/services/StockRepository";

export const ZRA_DETAILS = 'zra_details'
export const DEFAULT_STORE = 'default_store'
export const ALL_STORES = 'all_stores'
export const FRANCHISE_SETTINGS = 'franchise_settings'
export const REGISTRATION_DETAILS = 'registration_details'

export const ADMIN = 'admin'
export const CATEGORIES = 'categories'
export const AllUsers = 'all_users'
export const AllSecurities = 'securities'
export const CurrentUser = 'current_user'
export const STOCK = 'stock'

export const PAYMENT_METHODS = 'payment_methods'
export const STOCK_SETTING = 'stock_setting'
export const TAX_LABELS = 'taxLabels'

export const HAS543 = 'has543'
export const HAS_LOYALTY = 'hasLoyalty'
export const HASKAZANG = 'hasKazang'
export const KAZANGSESSION = 'kazangSession'
export const KAZANGPRODUCTS = 'kazangProductList'
export const STORE_CURRENCIES = 'storeCurrencies'

export const FLOAT = 'float'
export const DEVICE_SETTINGS = 'device_settings'
export const SAVED_TABLES = 'saved_tables'

export const BASE_CURRENCY = 'base_currency'
export const ZED_MOBILE_STATUS = 'zedMobileStatus'

export const DISCOUNT_SPECIALS = 'discountSpecials'
export const MULTI_SPECIALS = 'multiSpecials'
export const COMBO_SPECIALS = 'comboSpecials'
export const SYSTEM_SETTINGS = 'systemSettings'

export const SUPERVISOR = 'supervisor'
export const STORE_EXPIRY = 'store_expiry'
export const FLOAT_PENDING = 'float_pending'

/**
 * Storage key for the deeplink session minted by
 * {@code POST /posAuth/{DB}/login} on Franchise V2 (port 7082).
 * Carries only an opaque {@code sessionId} + {@code sessionExpiresAt}
 * - never the user's plaintext password. Replaces the legacy
 * `?auth=` deeplink payload which was base64-encoded JSON containing
 * the password. See
 * `docs/exec-plans/active/2026-06-22-deeplink-session-rollout.md`.
 */
export const CURRENT_SESSION = 'current_session'

export const OFFLINE_CUSTOMERS = 'offline_customers'
export const CUSTOMER_ID_MAP = 'customer_id_map'
export const COOKING_INSTRUCTIONS = 'cooking_instructions'
export const COOKING_INSTRUCTIONS_BY_PLU = 'cooking_instructions_by_plu'
export const STOCK_SYNC_STATE = 'stock_sync_state'

const notEmpty = (val) => {
    if (!val) return false
    return val !== ''
}
const normalizeText = (value) => String(value || '').toLowerCase();
const sortStockByDescription = (a, b) => String(a?.Description || '').localeCompare(String(b?.Description || ''));
const extractStockUpdatedAt = (item) => String(
    item?.updated_at ||
    item?.UpdatedAt ||
    item?.updatedAt ||
    item?.LastUpdated ||
    ''
);

const normalizeStockDeltas = (entries = []) => {
    const source = Array.isArray(entries) ? entries : [];
    const byPlu = new Map();

    for (const entry of source) {
        const plu = String(entry?.plu ?? entry?.PLU ?? entry?.Plu ?? '').trim();
        if (!plu) continue;
        const qty = Number(entry?.qtyDelta ?? entry?.qty ?? entry?.Qty ?? entry?.QTY);
        if (!Number.isFinite(qty) || qty <= 0) continue;

        const key = plu.toLowerCase();
        const prev = byPlu.get(key);
        if (prev) {
            prev.qtyDelta = Number(prev.qtyDelta) + qty;
        } else {
            byPlu.set(key, { plu, qtyDelta: qty });
        }
    }

    return Array.from(byPlu.values());
};

const normalizeStockQuery = (query = {}) => ({
    description: normalizeText(query.description),
    category: normalizeText(query.category),
    subCategory: normalizeText(query.subCategory),
    sub2Category: normalizeText(query.sub2Category),
});

const matchesCategoryFilters = (item, normalizedQuery) => {
    if (notEmpty(normalizedQuery.category) && normalizeText(item?.Department) !== normalizedQuery.category) {
        return false;
    }
    if (notEmpty(normalizedQuery.subCategory) && normalizeText(item?.SubDep) !== normalizedQuery.subCategory) {
        return false;
    }
    if (notEmpty(normalizedQuery.sub2Category) && normalizeText(item?.Subdep2) !== normalizedQuery.sub2Category) {
        return false;
    }
    return true;
};

const matchesStockSearchText = (item, normalizedDescription, includeDescription2 = true) => {
    if (!normalizedDescription) return true;
    if (normalizeText(item?.Description).includes(normalizedDescription)) return true;
    if (includeDescription2 && normalizeText(item?.Description2).includes(normalizedDescription)) return true;
    if (normalizeText(item?.PLU).includes(normalizedDescription)) return true;
    if (normalizeText(item?.barcode).includes(normalizedDescription)) return true;
    return false;
};

const createDefaultStockSetting = (plu = '') => ({
    plu: String(plu || ''),
    cookingLink: false,
    mtv: 0.00,
    printer: null,
    askForPrice: false,
    askForDescription: false,
    askForBoth: false,
    NonStock: false,
    tourism: false,
    getQtyFromPrice: false,
    password_protected: false,
    scale: false
});

const buildStockSettingMap = (settings = []) => {
    const source = Array.isArray(settings) ? settings : [];
    return source.reduce((acc, setting) => {
        const key = String(setting?.plu ?? setting?.PLU ?? '').toLowerCase();
        if (!key) return acc;
        acc[key] = setting;
        return acc;
    }, {});
};

const encryptAdmin = (val) => {
    return window.btoa(val);
}
const decryptAdmin = (val) => {
    if (!val) return undefined;
    if (val.toString().charAt(0) === `"`) {
        val = val.toString().slice(1, -1);
    }
    return JSON.parse(window.atob(val))
}

// Storage method helpers - using SQLite via storageAdapter
const getValue = async (key, storeName = 'main_store') => {
    return getValueAdapter(key, storeName);
};

const setValue = async (key, value, storeName = 'main_store') => {
    return setValueAdapter(key, value, storeName);
};



export const Storage = {
    invalidateStockCache: async () => {
        _stockCache = null;
        _fastLineCache = null;
        _stockSettingListCache = null;
        _stockSettingMapCache = null;
    },
    // Float pending helpers: track per-user pending float requirement when offline
    setFloatPending: async (username, pending = true) => {
        try {
            const map = (await getValue(FLOAT_PENDING)) || {};
            if (pending) {
                map[username] = true;
            } else if (map && Object.prototype.hasOwnProperty.call(map, username)) {
                delete map[username];
            }
            await setValue(FLOAT_PENDING, map);
        } catch (e) {
            console.warn('Failed to set float pending flag:', e);
        }
    },
    getFloatPending: async (username) => {
        try {
            const map = (await getValue(FLOAT_PENDING)) || {};
            return !!(map && map[username]);
        } catch (e) {
            return false;
        }
    },
    clearFloatPending: async (username) => {
        return Storage.setFloatPending(username, false);
    },
    /**
     * Store-only sign-out disk wipe.
     *
     * KEEP (franchise Welcome footprint):
     *   - admin (email/password/deviceId from FranchiseLoginStep / appLogin)
     *   - all_stores (store picker list from franchise login)
     *
     * CLEAR (bound when a store is selected and everything loaded after):
     *   default_store, cashier session, staff/security, stock, store config,
     *   float/tables/cart crumbs, admin-OTP localStorage keys.
     *
     * Why: after Sign out the device must land on store picker with franchise
     * still logged in — not Welcome and not PIN for the previous store.
     * default_store is the gate LoginView uses to skip straight to PIN.
     */
    logOutofStore: async () => {
        const emptyUser = {
            Type: "",
            Username: "",
            id: "",
            Surname: "",
            Name: "",
            Password: ""
        };

        // Selected store binding (the key that rehydrates "still in a store")
        try {
            await setValue(DEFAULT_STORE, null);
            store.commit('setDefaultStore', null);
        } catch (err) {
            console.warn('[Storage] clear default_store failed:', err?.message || err);
        }

        // Cashier identity + deeplink session (store-scoped token)
        await Storage.setCurrentUser(emptyUser);
        await Storage.clearCurrentSession();

        // Staff / security caches for the previous store
        await Storage.setAllUsers([]);
        try { await setValue('AllUsersMeta', null); } catch (_) { /* ignore */ }
        try { await Storage.setAllSecurities([]); } catch (_) { /* ignore */ }
        try { await setValue('AllSecuritiesMeta', null); } catch (_) { /* ignore */ }
        try { await Storage.setSupervisor(null); } catch (_) { /* ignore */ }

        // Catalogue is device-global SQL — wipe so the next store cannot see
        // another store's stock or dirty flags.
        try {
            await Storage.clearLocalStock();
        } catch (err) {
            console.warn('[Storage] clearLocalStock on logout failed:', err?.message || err);
        }

        // Store-scoped config / feature caches (setValue null = delete row)
        const storeScopedKeys = [
            CATEGORIES,
            PAYMENT_METHODS,
            BASE_CURRENCY,
            STORE_CURRENCIES,
            DISCOUNT_SPECIALS,
            MULTI_SPECIALS,
            COMBO_SPECIALS,
            TAX_LABELS,
            SYSTEM_SETTINGS,
            FRANCHISE_SETTINGS,
            REGISTRATION_DETAILS,
            STORE_EXPIRY,
            DEVICE_SETTINGS,
            FLOAT,
            FLOAT_PENDING,
            SAVED_TABLES,
            HAS543,
            HAS_LOYALTY,
            HASKAZANG,
            KAZANGSESSION,
            KAZANGPRODUCTS,
            ZED_MOBILE_STATUS,
            ZRA_DETAILS,
            OFFLINE_CUSTOMERS,
            CUSTOMER_ID_MAP,
            COOKING_INSTRUCTIONS,
            COOKING_INSTRUCTIONS_BY_PLU,
            SUPERVISOR,
        ];
        await Promise.all(storeScopedKeys.map(async (key) => {
            try {
                await setValue(key, null);
            } catch (err) {
                console.warn(`[Storage] clear ${key} on logout failed:`, err?.message || err);
            }
        }));

        // Admin OTP path crumbs + per-store offline invoice sequence hint
        try {
            localStorage.removeItem('users');
            localStorage.removeItem('supportAgent');
            localStorage.removeItem('latestOfflineInvoiceSequence');
            localStorage.removeItem('currentSaleLines');
            localStorage.removeItem('apiKey');
        } catch (_) { /* ignore */ }

        // Do NOT clear admin or all_stores — franchise Welcome footprint.
    },

    /**
     * Remove persisted stock, dirty flags, and sync checkpoint from
     * device storage. Call on sign-out and when StoreDB changes.
     */
    clearLocalStock: async () => {
        _stockCache = null
        _fastLineCache = null
        _stockSettingListCache = null
        _stockSettingMapCache = null
        try {
            await StockRepository.clearAllStock()
        } catch (error) {
            console.error('[Storage] clearAllStock failed:', error)
            throw error
        }
        try {
            await Storage.setStockSyncState({})
        } catch (err) {
            console.warn('[Storage] clear stock_sync_state failed:', err?.message || err)
        }
        try {
            await setValue(STOCK_SETTING, null)
        } catch (err) {
            console.warn('[Storage] clear stock_setting failed:', err?.message || err)
        }
    },
    getCurrentStoreDB: async () => {
        if (!store.getters.isHydrated) {
            await new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (store.getters.isHydrated) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 50);  // Check every 50ms
            });
        }
        let defaultStore = store.getters.getDefaultStore;
        if (defaultStore) {
            if (!defaultStore) {
                axios.get(reportTomcatApi + `getFranchiseByDB?DB=${defaultStore.StoreDB}`)
                    .then(({ data }) => store.commit(`setAdmin`, data))
            }
            return defaultStore.StoreDB;
        }
        if (!window.androidMode) {
            let storeDB;
            try {
                let response = await axios.get(hardwareApi + `utils/getStoreDB`);
                if (response.status === 404) {
                    responses.showWarning(`Request for hardware service update`)
                    return null;
                }
                storeDB = response.data;
                if (storeDB && storeDB !== 'none') {
                    console.trace(`Called`)
                    return axios.get(reportTomcatApi + `getStoresByDB?DB=${storeDB}`)
                        .then(async ({ data }) => {
                            store.commit(`setRegisteredStores`, data);
                            let currentStore = data.find(s => s.StoreDB === storeDB);
                            if (currentStore) {
                                await Storage.setDefaultStore(currentStore);
                                axios.get(reportTomcatApi + `getFranchiseByDB?DB=${storeDB}`)
                                    .then(({ data }) => store.commit(`setAdmin`, data))
                                if (router.currentRoute.name === `login` || router.currentRoute.name === `poslogin` || router.currentRoute.name === `setup`) {
                                    router.push({ name: `poslogin` })
                                }
                                return currentStore.StoreDB;
                            }
                        })
                }
            } catch (e) {
            }
        }
        return null;
    },
    setAdminUser: async (user) => {
        const encrypted = encryptAdmin(JSON.stringify(user));
        await setValue(ADMIN, encrypted, 'admin_users');
        store.commit('setAdmin', user);
    },
    getAdminUser: async () => {
        await Storage.waitForHydrate();
        let value = store.getters.getAdmin;
        if (value) return value;

        try {
            // Check SQLite/storage first
            value = await getValue(ADMIN, 'admin_users');
            if (value) return decryptAdmin(value);
        } catch (error) {
            console.error('Storage access error:', error);
        }
        return null;
    },

    setAllStores: async (payload) => {
        // store/index.js state: registeredStores
        await setValue(ALL_STORES, payload);
        store.commit('setRegisteredStores', payload);
    },

    getAllStores: async () => {
        let value = store.getters.getRegisteredStores;
        if (value) return value;
        value = await getValue(ALL_STORES);
        return value;
    },

    setFranchiseSettings: async (payload) => {
        await setValue(FRANCHISE_SETTINGS, payload);
        store.commit('setFranchiseSettings', payload);
    },

    setRegistrationDetails: async (payload) => {
        await setValue(REGISTRATION_DETAILS, payload);
        store.commit('setRegistrationDetails', payload);
    },

    getFranchiseSettings: async () => {
        let value = store.getters.getFranchiseSettings;
        if (value) return value;
        value = await getValue(FRANCHISE_SETTINGS);
        return value;
    },

    getRegistrationDetails: async () => {
        let value = store.getters.getRegistrationDetails;
        if (value) return value;
        value = await getValue(REGISTRATION_DETAILS);
        return value;
    },

    setStoreExpiry: async (expiryDate) => {
        // Store expiry date as ISO string for offline login validation
        await setValue(STORE_EXPIRY, expiryDate);
    },

    getStoreExpiry: async () => {
        // Retrieve cached expiry date for offline login validation
        const value = await getValue(STORE_EXPIRY);
        return value;
    },

    setZRADetails: async (payload) => {
        await setValue(ZRA_DETAILS, payload);
    },

    setCategories: async (categories) => {
        await setValue(CATEGORIES, categories);
        store.commit('setCategories', categories);
    },

    getCategories: async () => {
        let value = store.getters.getCategories;
        if (value) return value;
        value = await getValue(CATEGORIES);
        return value;
    },

    setHas543: async (has543) => {
        await setValue(HAS543, has543);
        store.commit('setHas543', has543);
    },
    getHas543: async () => {
        return getValue(HAS543);
    },




    setHasKazang: async (payload) => {
        await setValue(HASKAZANG, payload);
        store.commit('setHasKazang', payload);
    },

    getHasKazang: async () => {
        const value = await getValue(HASKAZANG);
        return JSON.parse(value);
    },

    getZRADetails: async () => {
        const value = await getValue(ZRA_DETAILS);
        return JSON.parse(value);
    },
    setDefaultStore: async (defaultStore) => {
        await setValue(DEFAULT_STORE, defaultStore);
        store.commit('setDefaultStore', defaultStore);
    },
    getDefaultStore: async () => {
        if (!store.getters.isHydrated) {
            await new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (store.getters.isHydrated) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 50);  // Check every 50ms
            });
        }
        let value = store.getters.getDefaultStore;
        if (value) {
            if (!value) {
                axios.get(reportTomcatApi + `getFranchiseByDB?DB=${value.StoreDB}`)
                    .then(({ data }) => store.commit(`setAdmin`, data))
            }
            setTimeout(() => {
                let name = router.currentRoute.name;
                if (name === 'login' || name === 'poslogin' || name === 'setup') {
                    router.push({ name: 'poslogin' }).catch((err) => {
                        if (err.name !== 'NavigationDuplicated') {
                            console.error(err)
                        }
                    })
                }
            }, 0)
            return value;
        }
        value = await getValue(DEFAULT_STORE);
        if (value) {
            if (value) {
                axios.get(reportTomcatApi + `getFranchiseByDB?DB=${value.StoreDB}`)
                    .then(({ data }) => store.commit(`setAdmin`, data))
            }
            setTimeout(() => {
                let name = router.currentRoute.name;
                if (name === 'login' || name === 'setup') {
                    router.push({ name: 'poslogin' }).catch((err) => {
                        if (err.name !== 'NavigationDuplicated') {
                            console.error(err)
                        }
                    })
                }
            }, 0)
            return value;
        }
        if (!window.androidMode) {
            let storeDB;
            try {
                let response = await axios.get(hardwareApi + `utils/getStoreDB`);
                if (response.status === 404) {
                    responses.showWarning(`Request for hardware service update`)
                    return null;
                }
                storeDB = response.data;
                if (storeDB && storeDB !== 'none') {
                    console.trace(`Called`)
                    return axios.get(reportTomcatApi + `getStoresByDB?DB=${storeDB}`)
                        .then(async ({ data }) => {
                            store.commit(`setRegisteredStores`, data);
                            let currentStore = data.find(s => s.StoreDB === storeDB);
                            if (currentStore) {
                                await Storage.setDefaultStore(currentStore);;
                                axios.get(reportTomcatApi + `getFranchiseByDB?DB=${storeDB}`)
                                    .then(({ data }) => store.commit(`setAdmin`, data))
                                setTimeout(() => {
                                    let name = router.currentRoute.name;
                                    if (name === 'login' || name === 'setup') {
                                        router.push({ name: 'poslogin' }).catch((err) => {
                                            if (err.name !== 'NavigationDuplicated') {
                                                console.error(err)
                                            }
                                        })
                                    }
                                }, 0)

                                return currentStore;
                            }
                        })
                }
            } catch (e) {
            }
        }
        return null;
    },

    getCurrentStoreID: async () => {
        let value = store.getters.getFranchiseSettings;
        if (value) return value.StoreId;
        value = await getValue(FRANCHISE_SETTINGS);
        if (!value) return null;
        return value.StoreId;
    },

    setAllUsers: async (users) => {
        // store/module/userModule.js state: users
        await setValue(AllUsers, users);
        // Store metadata with timestamp for offline login validation
        await setValue('AllUsersMeta', {
            lastSyncedAt: new Date().toISOString()
        });
        store.commit('setAllUsers', users);
    },
    getAllUsers: async () => {
        let value = store.getters.getUsers;
        if (value) return value;
        value = await getValue(AllUsers);
        return value;
    },
    getAllUsersMeta: async () => {
        return await getValue('AllUsersMeta');
    },
    setAllSecurities: async (users) => {
        await setValue(AllSecurities, users);
        // Store metadata with timestamp for offline permission checks
        await setValue('AllSecuritiesMeta', {
            lastSyncedAt: new Date().toISOString()
        });
        store.commit('setAllSecurities', users);
    },
    getAllSecurities: async () => {
        let value = store.getters.getSecurities;
        if (value) return value;
        value = await getValue(AllSecurities);
        return value;
    },
    getAllSecuritiesMeta: async () => {
        return await getValue('AllSecuritiesMeta');
    },

    setCurrentUser: async (currentUser) => {
        await setValue(CurrentUser, currentUser);
        store.commit('setCurrentUser', currentUser);
    },
    getCurrentUser: async () => {
        const inMemory = store.getters.getCurrentUser;
        const hasInMemoryUser = !!(
            inMemory &&
            inMemory.Username &&
            String(inMemory.Username).trim().length > 0
        );
        if (hasInMemoryUser) return inMemory;

        const persisted = await getValue(CurrentUser);
        const hasPersistedUser = !!(
            persisted &&
            persisted.Username &&
            String(persisted.Username).trim().length > 0
        );

        if (hasPersistedUser) {
            store.commit('setCurrentUser', persisted);
            return persisted;
        }

        return inMemory || persisted;
    },

    /**
     * Persist the deeplink session payload returned by
     * {@code POST /posAuth/{DB}/login}. The payload is written to a
     * single dedicated slot ({@code CURRENT_SESSION}) and tagged
     * informational-only with the {@code storeDb} it was minted for.
     *
     * <p>The {@code storeDb} tag is NOT enforced on read - see
     * {@link getCurrentSession}. Store-scoped revocation is enforced
     * server-side by the {@code GET /posAuth/{DB}/session/{id}}
     * endpoint, which only finds rows in {@code <DB>.loginaudit}.
     *
     * <p>Payload shape (subset used by the backoffice-module deeplink
     * opener):
     * <pre>
     * {
     *   sessionId:        string  // 64-char hex from SecureRandom
     *   sessionExpiresAt: string  // ISO-8601 datetime
     *   username:         string  // emp.Username
     *   empId:            string  // emp.id
     *   storeDb:          string  // informational - minted-for store
     * }
     * </pre>
     *
     * @param {Object} payload session payload from the login response
     * @param {string} storeDb the StoreDB this session was issued for (informational)
     */
    setCurrentSession: async (payload, storeDb) => {
        const sessionId = payload && payload.sessionId;
        const sessionIdPrefix = sessionId && typeof sessionId === 'string' ? sessionId.slice(0, 8) + '...' : 'none';
        const expiresAt = payload && payload.sessionExpiresAt ? payload.sessionExpiresAt : 'none';
        // eslint-disable-next-line no-console
        console.log(`[deeplink][Storage] setCurrentSession storeDb=${storeDb || 'null'} sessionIdPrefix=${sessionIdPrefix} expiresAt=${expiresAt}`);
        const value = Object.assign({}, payload || {}, { storeDb: storeDb || null });
        await setValue(CURRENT_SESSION, value);
    },

    /**
     * Read the persisted deeplink session. Always returns the latest
     * session in storage regardless of {@code expectedStoreDb}.
     *
     * <p>Why we don't gate on storeDb here: the previous implementation
     * returned {@code null} when the persisted session's {@code storeDb}
     * did not match the current default store's {@code StoreDB}. That
     * caused false negatives when:
     * <ul>
     *   <li>The cashier logged into store A, then a code path
     *       (vuex-persistedstate hydration, default-store change, etc.)
     *       made the default store B without re-running {@code posLogin}
     *       for B. The session for A was still valid but invisible.</li>
     *   <li>The cashier logged into A, then the device default was
     *       switched, then back to A — the session existed but the
     *       predicate rejected it.</li>
     * </ul>
     *
     * <p>Store-scoped revocation is enforced by the backend:
     * {@code GET /posAuth/{DB}/session/{id}} only finds rows in
     * {@code <DB>.loginaudit} matching the {@code DB} URL parameter. A
     * session minted for store A cannot be redeemed against store B
     * even if the URL is forged. The frontend cross-check was
     * redundant and was causing this exact bug.
     *
     * <p>{@code expectedStoreDb} is retained for diagnostic logging only.
     *
     * @param {string} [expectedStoreDb] informational only - logged, not enforced
     * @returns {Promise<?Object>} the session payload or null when absent
     */
    getCurrentSession: async (expectedStoreDb) => {
        const persisted = await getValue(CURRENT_SESSION);
        if (!persisted) {
            // eslint-disable-next-line no-console
            console.log(`[deeplink][Storage] getCurrentSession expectedStoreDb=${expectedStoreDb || 'null'} persisted=null persistedStoreDb=undefined match=n/a`);
            return null;
        }
        // eslint-disable-next-line no-console
        console.log(`[deeplink][Storage] getCurrentSession expectedStoreDb=${expectedStoreDb || 'null'} persisted=${JSON.stringify(persisted)} persistedStoreDb=${persisted.storeDb || 'undefined'} match=n/a (gate removed 2026-06-24)`);
        return persisted;
    },

    /**
     * Clear the persisted deeplink session. Called on logout / store
     * switch to prevent a stale token being used to open BackOffice.
     *
     * Implementation note: relies on the storageAdapter convention that
     * `setValue(key, null)` is treated as "delete the row" rather than
     * an INSERT of a null value (which would violate the SQLite
     * `main_store.value NOT NULL` constraint). See the convention note
     * at the top of this file.
     */
    clearCurrentSession: async () => {
        const caller = (new Error().stack || '').split('\n')[2] || 'unknown';
        // eslint-disable-next-line no-console
        console.log(`[deeplink][Storage] clearCurrentSession called from ${caller.trim()}`);
        await setValue(CURRENT_SESSION, null);
    },

    stockByPLU: async (stock) => {
        _stockCache = null; // Invalidate cache
        _fastLineCache = null;
        const payload = Array.isArray(stock) ? stock : [];
        try {
            await StockRepository.replaceAllStock(payload);
        } catch (error) {
            console.error('[Storage] SQL stock replace failed:', error);
            throw error;
        }
    },
    upsertStockBatch: async (stockBatch) => {
        _stockCache = null;
        _fastLineCache = null;
        const payload = Array.isArray(stockBatch) ? stockBatch : [];
        try {
            await StockRepository.upsertStockBatch(payload);
        } catch (error) {
            const message = String(error?.message || '').toLowerCase();
            if (message.includes('within a transaction')) {
                try {
                    await new Promise(resolve => setTimeout(resolve, 30));
                    await StockRepository.upsertStockBatch(payload);
                    return;
                } catch (retryError) {
                    console.error('[Storage] SQL stock batch upsert retry failed:', retryError);
                    throw retryError;
                }
            }
            console.error('[Storage] SQL stock batch upsert failed:', error);
            throw error;
        }
    },
    /**
     * Apply a single page from the paginated `/stock/{DB}/page` endpoint.
     *
     * Reconciles against dirty stock (rows with pending sales are
     * skipped), persists the surviving updates in a batched upsert,
     * and clears the dirty flag for rows the server has now caught up
     * with. Returns `{ applied, skipped }` so the caller can commit
     * only the surviving items into Vuex state.
     *
     * Always drops in-memory stock/FastLine caches after a write: the
     * repository upserts SQL directly and would otherwise leave
     * `_stockCache` frozen on page-0 data so Phase 2 end refresh
     * rebuilt the FastLine grid from a stale list.
     *
     * @param {Array<Object>} items page of raw server stock items
     * @returns {Promise<{applied: Array<Object>, skipped: Array<Object>}>}
     */
    applyPaginatedStockPage: async (items) => {
        const result = await StockRepository.applyPaginatedStockPage(items);
        // SQL changed under us — force next getAllStock/getFastLine to re-read.
        _stockCache = null;
        _fastLineCache = null;
        return result;
    },
    /**
     * Count rows in `stock_items`. Used by the sync integrity report to
     * warn when the local count diverges from the server's reported
     * total. Wraps StockRepository.countAllStock so callers can depend
     * on the Storage abstraction without importing the repo directly.
     *
     * Plan §B5.
     *
     * @returns {Promise<number|null>}
     */
    countAllStock: async () => {
        return StockRepository.countAllStock();
    },
    /**
     * Wipe local `stock_items` for a full paginated refill.
     * Does not touch `stock_dirty` (offline pending sales).
     * Invalidates in-memory stock caches so the next read hits SQL.
     *
     * @returns {Promise<void>}
     */
    clearStockItemsOnly: async () => {
        _stockCache = null;
        _fastLineCache = null;
        try {
            await StockRepository.clearStockItemsOnly();
        } catch (error) {
            console.error('[Storage] clearStockItemsOnly failed:', error);
            throw error;
        }
    },
    setStockSyncState: async (payload) => {
        await setValue(STOCK_SYNC_STATE, payload || {});
    },
    getStockSyncState: async () => {
        const value = await getValue(STOCK_SYNC_STATE);
        return value || {};
    },
    applyLocalStockDeltas: async (stockDeltas, options = {}) => {
        await Storage.invalidateStockCache();
        return StockRepository.applyLocalStockDeltas(normalizeStockDeltas(stockDeltas), options);
    },
    markStockDirtyPending: async (stockDeltas, saleId, options = {}) => {
        return StockRepository.markDirtyPending(normalizeStockDeltas(stockDeltas), saleId, options);
    },
    releaseStockDirtyPending: async (stockDeltas, saleId, options = {}) => {
        return StockRepository.releaseDirtyPending(normalizeStockDeltas(stockDeltas), saleId, options);
    },
    getDirtyStockMap: async (pluList = []) => {
        return StockRepository.getDirtyStockMap(pluList);
    },
    clearDirtyStock: async (pluList = []) => {
        return StockRepository.clearDirtyStock(pluList);
    },
    reconcileServerStockUpdates: async (updates = []) => {
        const source = Array.isArray(updates) ? updates : [];
        if (source.length === 0) {
            return { applyUpdates: [], skippedUpdates: [], clearDirtyPlu: [] };
        }

        const pluList = source
            .map(item => String(item?.PLU ?? item?.plu ?? '').trim())
            .filter(Boolean);

        const dirtyMap = await StockRepository.getDirtyStockMap(pluList);
        const applyUpdates = [];
        const skippedUpdates = [];
        const clearDirtyPlu = [];

        for (const item of source) {
            const plu = String(item?.PLU ?? item?.plu ?? '').trim();
            if (!plu) continue;

            const key = plu.toLowerCase();
            const dirty = dirtyMap[key];

            // Branch C — no local pending change. Server is authoritative.
            // Reason: the cashier has no offline sale on this PLU that the
            // server hasn't seen yet. Apply the server row wholesale.
            if (!dirty) {
                applyUpdates.push(item);
                continue;
            }

            const pendingSales = Number(dirty.pending_sales || 0);

            // Branch A — pending offline sale. Apply non-qty fields only;
            // preserve the local qty (the server hasn't seen this sale yet).
            // The dirty row stays until the sale upload calls F2 (clearDirty).
            if (pendingSales > 0) {
                const preservedQty = Number(dirty.qty_delta || 0);
                const safeQty = Number.isFinite(preservedQty) ? preservedQty : 0;
                const { QTY, actualQty, qty, ...nonQty } = item;
                applyUpdates.push({
                    ...nonQty,
                    QTY: safeQty,
                    actualQty: safeQty,
                    qty: safeQty,
                });
                skippedUpdates.push({ plu, reason: 'pending-local-sales' });
                continue;
            }

            // Branch B — stale dirty flag. The sale already uploaded but the
            // dirty flag wasn't cleared (the live bug). Apply server wholesale
            // and clear the dirty row in the same transaction so the cashier
            // never sees this trap again.
            applyUpdates.push(item);
            clearDirtyPlu.push(plu);
        }

        return { applyUpdates, skippedUpdates, clearDirtyPlu };
    },
    /**
     * Sweep stale `stock_dirty` rows whose `pending_sales = 0`.
     *
     * Defense-in-depth: the simplified reconciliation (Branch B) clears
     * these rows automatically when the server returns the PLU, but any
     * PLU the server hasn't returned since the dirty row went stale would
     * stay stuck. A periodic sweep self-heals those without requiring a
     * logout. Gated to run at most once per 7 days.
     *
     * @returns {Promise<number>} rows swept
     */
    sweepStaleDirtyStock: async () => {
        const STORAGE_KEY = 'last_swept_dirty_at';
        const SWEEP_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;
        try {
            const lastSweptAt = await getValue(STORAGE_KEY);
            const lastMs = lastSweptAt ? new Date(lastSweptAt).getTime() : 0;
            if (Number.isFinite(lastMs) && (Date.now() - lastMs) < SWEEP_INTERVAL_MS) {
                return 0;
            }
            const swept = await StockRepository.sweepStaleDirtyStock();
            if (swept > 0) {
                // eslint-disable-next-line no-console
                console.warn(`[Stock Hygiene] swept ${swept} stale dirty row(s) (pending_sales=0)`);
            }
            await setValue(STORAGE_KEY, new Date().toISOString());
            return swept;
        } catch (err) {
            console.warn('[Stock Hygiene] sweepStaleDirtyStock failed:', err?.message || err);
            return 0;
        }
    },
    getAllStock: async () => {
        if (_stockCache) return _stockCache;

        let stock = [];
        try {
            stock = await StockRepository.getAllStock();
        } catch (error) {
            console.error('[Storage] SQL stock read failed:', error);
            throw error;
        }

        _stockCache = Array.isArray(stock) ? stock : [];
        return stock;
    },

    getByPlu: async (plu) => {
        const pluKey = String(plu || '');
        if (!pluKey) return null;
        if (_stockCache) {
            const fromCache = _stockCache.find(item => String(item?.PLU || '').toLowerCase() === pluKey.toLowerCase());
            if (fromCache) return fromCache;
        }
        try {
            const row = await StockRepository.getByPlu(pluKey);
            return row || null;
        } catch (error) {
            console.warn('[Storage] SQL getByPlu failed, using in-memory fallback:', error);
            const stock = await Storage.getAllStock();
            return stock.find(item => String(item?.PLU || '').toLowerCase() === pluKey.toLowerCase()) || null;
        }
    },
    getByBarcode: async (barcode) => {
        const key = String(barcode || '');
        if (!key) return null;
        try {
            return await StockRepository.getByBarcode(key);
        } catch (error) {
            console.warn('[Storage] getByBarcode failed:', error);
            return null;
        }
    },
    replaceAllItemBarcodes: async (items) => {
        try {
            return await StockRepository.replaceAllItemBarcodes(items);
        } catch (error) {
            console.error('[Storage] replaceAllItemBarcodes failed:', error);
            throw error;
        }
    },
    getFastLine: async () => {
        if (_fastLineCache) return _fastLineCache;
        try {
            _fastLineCache = await StockRepository.getFastLine();
        } catch (error) {
            console.warn('[Storage] SQL fast line read failed, using in-memory fallback:', error);
            let stock = await Storage.getAllStock(); // Uses optimized cached getAllStock
            _fastLineCache = stock.filter(sc => sc.FastLine === 1);
        }
        return _fastLineCache;
    },
    searchStockDesc: async (query, limit = 5) => {
        const filters = {
            category: query?.category || '',
            subCategory: query?.subCategory || '',
            sub2Category: query?.sub2Category || '',
        };
        const text = query?.description || '';
        const safeLimit = Math.max(0, Number(limit) || 0);

        try {
            const { rows } = await StockRepository.searchStockPage(filters, text, safeLimit, 0, true);
            return rows;
        } catch (error) {
            console.warn('[Storage] SQL stock suggestion search failed, using in-memory fallback:', error);
            const stock = await Storage.getAllStock();
            const normalizedQuery = normalizeStockQuery(query);
            const result = [];

            for (const item of stock) {
                if (!item) continue;
                if (!matchesCategoryFilters(item, normalizedQuery)) continue;
                if (!matchesStockSearchText(item, normalizedQuery.description, true)) continue;
                result.push(item);
            }

            result.sort(sortStockByDescription);
            if (!Number.isFinite(limit) || limit <= 0) return result;
            return result.slice(0, limit);
        }
    },
    searchStock: async (query, limit = 50, offset = 0) => {
        const filters = {
            category: query?.category || '',
            subCategory: query?.subCategory || '',
            sub2Category: query?.sub2Category || '',
            description: '',
        };
        const text = query?.description || '';
        const safeLimit = Math.max(0, Number(limit) || 0);
        const safeOffset = Math.max(0, Number(offset) || 0);

        try {
            const result = text
                ? await StockRepository.searchStockPage(filters, text, safeLimit, safeOffset, false)
                : await StockRepository.queryStockPage(filters, safeLimit, safeOffset);
            store.commit('setStockLength', result.total);
            return result.rows;
        } catch (error) {
            console.warn('[Storage] SQL stock page query failed, using in-memory fallback:', error);
            const stock = await Storage.getAllStock();
            const normalizedQuery = normalizeStockQuery(query);
            const filtered = [];

            for (const item of stock) {
                if (!item) continue;
                if (!matchesCategoryFilters(item, normalizedQuery)) continue;
                if (!matchesStockSearchText(item, normalizedQuery.description, false)) continue;
                filtered.push(item);
            }

            store.commit('setStockLength', filtered.length);

            if (filtered.length === 0) return [];

            const paged = safeLimit > 0 ? filtered.slice(safeOffset, safeOffset + safeLimit) : filtered.slice(safeOffset);
            return paged.sort(sortStockByDescription);
        }
    },
    editStockItem: async payload => {
        _stockCache = null;
        _fastLineCache = null;
        try {
            await StockRepository.upsertStockBatch([payload]);
        } catch (error) {
            console.error('[Storage] SQL stock edit failed:', error);
            throw error;
        }
    },
    setFloat: async (payload) => {
        await setValue(FLOAT, payload)
        store.commit('setUserFloat', payload);
    },
    getFloat: async () => {
        let value = store.getters.getFloats;
        if (value) return value;
        value = await getValue(FLOAT);
        return value;
    },
    setDeviceSettings: async (payload) => {
        await setValue(DEVICE_SETTINGS, payload)
        store.commit('setDeviceSettings', payload)
    },

    getDeviceSettings: async () => {
        let value = store.getters.getDeviceSettings;
        if (value) return value;
        value = await getValue(DEVICE_SETTINGS);
        return value;
    },
    getAllTables: async () => {
        let value = store.getters.getAllTables;
        if (value) return value;
        value = await getValue(SAVED_TABLES);
        return value;
    },

    setTables: async (payload) => {
        await setValue(SAVED_TABLES, payload)
    },

    createTable: async (payload) => {
        let value = await getValue(SAVED_TABLES);
        if (!value) {
            value = {
                value: JSON.stringify([]),
            }
        }
        let tables = JSON.parse(value);
        if (!tables) {
            tables = []
        }
        tables.push(payload)
        await setValue(SAVED_TABLES, JSON.stringify(tables));
    },

    editTable: async (payload) => {
        const value = await getValue(SAVED_TABLES);
        let tables = JSON.parse(value);
        tables = tables.map(table => {
            if (table.SaleNum === payload.SaleNum) {
                return payload
            }
            return table;
        })
        await setValue(SAVED_TABLES, JSON.stringify(tables));
    },

    deleteTable: async (salenum) => {
        const value = await getValue(SAVED_TABLES);
        let tables = JSON.parse(value);
        tables = tables.filter(table => table.SaleNum !== salenum);
        await setValue(SAVED_TABLES, JSON.stringify(tables));
    },

    clear: async () => {
        await clear();
    },
    clearAll: async () => {
        try {
            await Storage.clearAll();
        } catch (error) {
            console.error('Failed to clear storage:', error);
        }
    },


    setStockSettings: async (stock) => {
        const safeStock = Array.isArray(stock) ? stock : [];
        await setValue(STOCK_SETTING, safeStock);
        _stockSettingListCache = safeStock;
        _stockSettingMapCache = buildStockSettingMap(safeStock);
        store.commit('setStockSetting', safeStock);
    },
    warmStockSettingsCache: async () => {
        if (_stockSettingMapCache && Object.keys(_stockSettingMapCache).length > 0) {
            return _stockSettingMapCache;
        }

        let settings = store.getters.getStockSetting;
        if ((!Array.isArray(settings) || settings.length === 0) && Array.isArray(_stockSettingListCache) && _stockSettingListCache.length > 0) {
            settings = _stockSettingListCache;
        }
        if (!Array.isArray(settings) || settings.length === 0) {
            settings = await getValue(STOCK_SETTING);
        }

        if (!Array.isArray(settings) || settings.length === 0) {
            _stockSettingListCache = [];
            _stockSettingMapCache = {};
            return _stockSettingMapCache;
        }

        _stockSettingListCache = settings;
        _stockSettingMapCache = buildStockSettingMap(settings);

        if (Array.isArray(store.getters.getStockSetting) && store.getters.getStockSetting.length === 0) {
            store.commit('setStockSetting', settings);
        }

        return _stockSettingMapCache;
    },
    getStockSetting: async (plu) => {
        const pluKey = String(plu || '').toLowerCase();
        if (!pluKey) return createDefaultStockSetting('');

        const byPluGetter = store.getters.getStockSettingByPlu;
        if (typeof byPluGetter === 'function') {
            const cached = byPluGetter(pluKey);
            if (cached) return cached;
        }

        if (_stockSettingMapCache && _stockSettingMapCache[pluKey]) {
            return _stockSettingMapCache[pluKey];
        }

        await Storage.warmStockSettingsCache();
        let settings = _stockSettingListCache;

        if (!Array.isArray(settings) || settings.length === 0) {
            return createDefaultStockSetting(plu);
        }

        if (!_stockSettingMapCache || _stockSettingListCache !== settings) {
            _stockSettingListCache = settings;
            _stockSettingMapCache = buildStockSettingMap(settings);
        }

        if (Array.isArray(store.getters.getStockSetting) && store.getters.getStockSetting.length === 0) {
            store.commit('setStockSetting', settings);
        }

        const match = _stockSettingMapCache[pluKey] || null;
        if (match) return match;

        return createDefaultStockSetting(plu);
    },

    setPaymentMethods: async (data) => {
        await setValue(PAYMENT_METHODS, data);
        store.commit('setPaymentMethods', data);
    },
    getPaymentMethods: async () => {
        let value = store.getters.getPaymentMethods;
        if (value) return value;
        value = await getValue(PAYMENT_METHODS);
        return value;
    },

    setKazangSession: async (data) => {
        await setValue(KAZANGSESSION, data);
        store.commit('setKazangSession', data);
    },
    getKazangSession: async () => {
        let value = store.getters.getKazangSessionID;
        if (value) return value;
        value = await getValue(KAZANGSESSION);
        return value;
    },

    setKazangProductList: async (data) => {
        await setValue(KAZANGPRODUCTS, data);
        store.commit('setKazangProductList', data);
    },
    getKazangProductList: async () => {
        let value = store.getters.getKazangProductList;
        if (value) return value;
        value = await getValue(KAZANGPRODUCTS);
        return value;
    },

    setBaseCurrency: async (data) => {
        await setValue(BASE_CURRENCY, data)
        store.commit('setBaseCurrency', data);
    },
    getBaseCurrency: async () => {
        let value = store.getters.getBaseCurrency;
        if (value) return value;
        value = await getValue(BASE_CURRENCY);
        return value;
    },

    setDiscountSpecials: async (payload) => {
        setValue(DISCOUNT_SPECIALS, payload)
        store.commit('setDiscountSpecials', payload);
    },
    getDiscountSpecials: async () => {
        let value = store.getters.getDiscountSpecials;
        if (value) return value;
        value = await getValue(DISCOUNT_SPECIALS);
        return value;
    },

    setMultiSpecials: async (payload) => {
        setValue(MULTI_SPECIALS, payload)
        store.commit('setMultiSpecials', payload);
    },
    getMultiSpecials: async () => {
        let value = store.getters.getMultiSpecials;
        if (value) return value;
        value = await getValue(MULTI_SPECIALS);
        return value;
    },

    setComboSpecials: async (payload) => {
        setValue(COMBO_SPECIALS, payload)
        store.commit('setComboSpecials', payload);
    },
    getComboSpecials: async () => {
        let value = store.getters.getComboSpecials;
        if (value) return value;
        value = await getValue(COMBO_SPECIALS);
        return value;
    },

    setTaxLabels: async (payload) => {
        setValue(TAX_LABELS, payload)
        store.commit('setTaxLabels', payload);
    },
    getAllTaxLabels: async () => {
        let value = store.getters.getTaxLabels;
        if (value) return value;
        value = await getValue(TAX_LABELS);
        return value;
    },

    setSystemSettings: async (payload) => {
        setValue(SYSTEM_SETTINGS, payload)
        store.commit('setSystemSettings', payload);
    },
    getSystemSettings: async () => {
        let value = store.getters.getSystemSettings;
        if (value) return value;
        value = await getValue(SYSTEM_SETTINGS);
        return value;
    },

    setHasLoyalty: async (payload) => {
        setValue(HAS_LOYALTY, payload)
        store.commit('setHasLoyalty', payload);
    },
    getHasLoyalty: async () => {
        let value = store.getters.getHasLoyalty;
        if (value) return value;
        value = await getValue(HAS_LOYALTY);
        return value;
    },

    setZedMobileStatus: async (payload) => {
        setValue(ZED_MOBILE_STATUS, payload)
        store.commit('setZedMobileStatus', payload);
    },
    getZedMobileStatus: async () => {
        let value = store.getters.getZedMobileStatus;
        if (value) return value;
        value = await getValue(ZED_MOBILE_STATUS);
        return value;
    },

    setStoreCurrencies: async (payload) => {
        setValue(STORE_CURRENCIES, payload)
        store.commit('setStoreCurrencies', payload);
    },
    getStoreCurrencies: async () => {
        let value = store.getters.getStoreCurrencies;
        if (value) return value;
        value = await getValue(STORE_CURRENCIES);
        return value;
    },

    waitForHydrate: async () => {
        if (!store.getters.isHydrated) {
            await new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (store.getters.isHydrated) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 50);
            });
        }
    },

    setSupervisor: async (supervisor) => {
        await setValue(SUPERVISOR, supervisor);
        store.commit('setSupervisor', supervisor);
    },

    getSupervisor: async () => {
        let value = store.getters.getSupervisor;
        if (value) return value;
        value = await getValue(SUPERVISOR);
        return value;
    },

    // Offline Customers helpers
    addOfflineCustomer: async (customer) => {
        try {
            const list = (await getValue(OFFLINE_CUSTOMERS)) || [];
            list.push(customer);
            await setValue(OFFLINE_CUSTOMERS, list);
            return true;
        } catch (e) {
            console.warn('Failed to add offline customer:', e);
            return false;
        }
    },
    getOfflineCustomers: async () => {
        try {
            return (await getValue(OFFLINE_CUSTOMERS)) || [];
        } catch (e) {
            return [];
        }
    },
    setOfflineCustomers: async (list) => {
        try {
            await setValue(OFFLINE_CUSTOMERS, list || []);
            return true;
        } catch (e) {
            console.warn('Failed to set offline customers list:', e);
            return false;
        }
    },
    getCustomerIdMap: async () => {
        try {
            return (await getValue(CUSTOMER_ID_MAP)) || {};
        } catch (e) {
            return {};
        }
    },
    setCustomerIdMap: async (map) => {
        try {
            await setValue(CUSTOMER_ID_MAP, map || {});
            return true;
        } catch (e) {
            console.warn('Failed to set customer id map:', e);
            return false;
        }
    },
    updateCustomerIdMap: async (tempId, realId) => {
        try {
            const map = (await Storage.getCustomerIdMap()) || {};
            map[tempId] = realId;
            await setValue(CUSTOMER_ID_MAP, map);
            return map;
        } catch (e) {
            console.warn('Failed to update customer id map:', e);
            return null;
        }
    },
    setCookingInstructions: async (payload) => {
        await setValue(COOKING_INSTRUCTIONS, payload || []);
    },
    getCookingInstructions: async () => {
        return (await getValue(COOKING_INSTRUCTIONS)) || [];
    },
    setCookingInstructionByPlu: async (plu, instruction) => {
        if (!plu) return;
        const map = (await getValue(COOKING_INSTRUCTIONS_BY_PLU)) || {};
        map[String(plu)] = instruction || null;
        await setValue(COOKING_INSTRUCTIONS_BY_PLU, map);
        try {
            localStorage.setItem(COOKING_INSTRUCTIONS_BY_PLU, JSON.stringify(map));
        } catch (e) { }
    },
    getCookingInstructionByPlu: async (plu) => {
        if (!plu) return null;
        let map = (await getValue(COOKING_INSTRUCTIONS_BY_PLU)) || {};
        if (!map || Object.keys(map).length === 0) {
            try {
                const raw = localStorage.getItem(COOKING_INSTRUCTIONS_BY_PLU);
                if (raw) map = JSON.parse(raw);
            } catch (e) { }
        }
        return (map && map[String(plu)]) || null;
    },
}
